import React from 'react';
import { CircularProgress, styled } from '@mui/material';
import { authColors, authFontFamily, authType } from 'app/pages/auth/shared/authTheme';
import { REPLAY_CHROME_CLASS } from 'sentryReplayPrivacy';

/**
 * Material 3 filled "square" button (the primary action on the auth screens).
 * Full-width by default; disabled + loading states match the Figma M3 spec.
 */

type AuthButtonVariant = 'filled' | 'outlined' | 'text';

const StyledButton = styled('button')<{ 'data-variant': AuthButtonVariant }>(
  ({ 'data-variant': variant }) => ({
    width: '100%',
    height: 48,
    borderRadius: 8,
    border: variant === 'outlined' ? `1px solid ${authColors.border}` : 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontFamily: authFontFamily,
    ...authType.button,
    color: variant === 'filled' ? authColors.white : authColors.primary,
    backgroundColor: variant === 'filled' ? authColors.primary : 'transparent',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover:not(:disabled)': {
      backgroundColor: variant === 'filled' ? authColors.primaryHover : 'rgba(35, 67, 127, 0.08)',
    },
    '&:disabled': {
      cursor: 'default',
      color: authColors.disabledText,
      borderColor: variant === 'outlined' ? authColors.disabledBg : undefined,
      backgroundColor: variant === 'filled' ? authColors.disabledBg : 'transparent',
    },
  })
);

export interface AuthButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: AuthButtonVariant;
  loading?: boolean;
}

export default function AuthButton({
  variant = 'filled',
  loading = false,
  disabled,
  children,
  className,
  ...rest
}: AuthButtonProps) {
  // A plain <button> matches no MUI unmask class: mark it as replay chrome so its label stays
  // visible inside masked auth containers.
  const chromeClassName = className ? `${REPLAY_CHROME_CLASS} ${className}` : REPLAY_CHROME_CLASS;
  return (
    <StyledButton data-variant={variant} disabled={disabled || loading} className={chromeClassName} {...rest}>
      {loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : children}
    </StyledButton>
  );
}
